export interface NavbarLinkLike {
  text: string;
  url: string;
  style?: string;
}

const NOOP_NAV_URLS = new Set(['/', '', '#', '#top']);

/** Common CTA labels that should route to signup, not the current/home page. */
const SIGNUP_CTA_PATTERN = /^(get started|sign up|start free|try free|create account)/i;

/**
 * When a navbar already has a normal Home link, a button-style CTA that still
 * points at `/` or `#` is almost always a misconfiguration — clicking it on
 * the home page does nothing visible.
 */
export function resolveNavbarLinkUrl(
  link: NavbarLinkLike,
  allLinks: NavbarLinkLike[],
  signupFallback = '/signup'
): string {
  const url = (link.url ?? '').trim() || '/';

  if (link.style !== 'button' || !NOOP_NAV_URLS.has(url)) {
    return link.url;
  }

  if (!SIGNUP_CTA_PATTERN.test((link.text ?? '').trim())) {
    return link.url;
  }

  const hasHomeNavLink = allLinks.some(
    other =>
      other !== link &&
      other.style !== 'button' &&
      NOOP_NAV_URLS.has((other.url ?? '').trim() || '/')
  );

  if (!hasHomeNavLink) {
    return link.url;
  }

  return signupFallback;
}

/** Collapse "Account Account" labels copied from desktop+mobile nav markup. */
export function collapseRepeatedLabel(text: string): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return t;

  const words = t.split(' ');
  if (words.length >= 2 && words.length % 2 === 0) {
    const half = words.length / 2;
    const a = words.slice(0, half).join(' ');
    const b = words.slice(half).join(' ');
    if (a.toLowerCase() === b.toLowerCase()) return a;
  }

  if (t.length >= 4 && t.length % 2 === 0) {
    const half = t.length / 2;
    if (t.slice(0, half).toLowerCase() === t.slice(half).toLowerCase()) {
      return t.slice(0, half);
    }
  }

  return t;
}

function pathToSlug(pathname: string): string {
  return (
    pathname
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-') || ''
  );
}

/**
 * Keep draft preview inside Compete Pages. Absolute URLs from an imported
 * site must not load in the preview iframe (X-Frame-Options).
 */
export function previewNavbarUrl(
  url: string,
  opts: { isPreviewMode: boolean; siteId?: string | null }
): string {
  if (!opts.isPreviewMode || !opts.siteId) return url;

  const trimmed = (url ?? '').trim();
  if (!trimmed || trimmed === '/' || trimmed === '#' || trimmed === '#top') {
    return `/sites/${opts.siteId}/preview`;
  }
  if (trimmed.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith(`/sites/${opts.siteId}/`)) {
    return trimmed;
  }

  let pathname = trimmed;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      pathname = new URL(trimmed).pathname || '/';
    }
  } catch {
    pathname = trimmed;
  }

  const slug = pathToSlug(pathname.startsWith('/') ? pathname : `/${pathname}`);
  if (!slug) return `/sites/${opts.siteId}/preview`;
  return `/sites/${opts.siteId}/preview/page/${slug}`;
}

/** Drop duplicate hrefs and collapse repeated labels for already-imported navs. */
export function uniqueNavbarLinks<T extends NavbarLinkLike>(links: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const link of links) {
    const text = collapseRepeatedLabel(link.text);
    const urlKey = (link.url ?? '').split('#')[0].replace(/\/+$/, '') || '/';
    // Same URL + same label is a desktop/mobile duplicate. Home + "Get
    // Started" both pointing at `/` must stay.
    const key = `${text.toLowerCase()}|${urlKey}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text === link.text ? link : { ...link, text });
  }
  return out;
}
